import { ComponentWithTooltip } from 'components/ComponentWithTooltip';
import type { WorkOrderPriceActValue } from 'gql/graphql';
import { DeviationEnum, WorkOrderPriceTypeEnum } from 'gql/graphql';
import { Badge, Button, FieldGroup, IconClose, IconEdit, Text } from 'uikit';

import cn from 'classnames';
import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { ActFormulaModal } from './ActFormulaModal';
import { useSetActFormulaValue } from './ActFormulaModal/hooks';

import Styles from './cellRenderer.module.css';

export const ActFormulaCellRenderer = ({
    currentAct,
    defaultValue,
    priceId,
    isDisabled,
    itemType,
    children,
    refreshDataHandler,
}: {
    currentAct: WorkOrderPriceActValue | undefined;
    defaultValue: string;
    priceId: number;
    isDisabled: boolean;
    itemType: WorkOrderPriceTypeEnum | undefined | null;
    children: ReactElement;
    refreshDataHandler?: (reloadRowsIds?: number[] | null) => void;
}) => {
    const { actVolumePlan, hasActFormula, actFormulaErrors, actFormulaDisplayText, actId, actVolumePlanDeviation } =
        currentAct ?? {};

    const [isEditOpened, setIsEditOpened] = useState(false);

    const isEditDisabled = useMemo(
        () => Boolean(!hasActFormula && actVolumePlan) || isDisabled,
        [actVolumePlan, hasActFormula, isDisabled],
    );

    const onSuccess = useCallback(
        (reloadRowsIds?: number[] | null) => {
            refreshDataHandler?.(reloadRowsIds);
            setIsEditOpened(false);
        },
        [refreshDataHandler],
    );

    const { setValueHandler } = useSetActFormulaValue({
        onSuccess,
        variables: { actKC2Id: actId, workOrderPriceId: priceId },
    });

    const clearFormulaHandler = useCallback(() => {
        setValueHandler(null);
    }, [setValueHandler]);

    const badgeContent = useMemo<any>(() => {
        const clearBtn = <Button iconRight={IconClose} onlyIcon size="xs" view="clear" onClick={clearFormulaHandler} />;

        if (actFormulaErrors) {
            const errorTooltip = actFormulaErrors?.map((e) => e).join('\n') ?? '';

            return (
                <div className={Styles.errorContainer}>
                    <ComponentWithTooltip tooltipText={errorTooltip}>
                        <Text size="2xs">Ошибка</Text>
                    </ComponentWithTooltip>
                    {clearBtn}
                </div>
            );
        }

        const withDeviation = actVolumePlanDeviation === DeviationEnum.Up;

        return (
            <>
                <ComponentWithTooltip className={Styles.badgeContent} tooltipText={actFormulaDisplayText ?? ''}>
                    <div className={Styles.badgeContentWrapper}>
                        {withDeviation && <span className={Styles.deviationUp}>▲</span>}
                        <Text size="2xs" view="secondary">
                            {defaultValue}
                        </Text>
                    </div>
                </ComponentWithTooltip>
                {clearBtn}
            </>
        );
    }, [actFormulaDisplayText, actFormulaErrors, clearFormulaHandler, defaultValue, actVolumePlanDeviation]);

    const toggleEditModal = useCallback(() => {
        setIsEditOpened((p) => !p);
    }, []);

    const isCoefficient = Boolean(
        itemType &&
            [
                WorkOrderPriceTypeEnum.ConstraintCoefficient,
                WorkOrderPriceTypeEnum.Coefficient,
                WorkOrderPriceTypeEnum.TechPartCoefficient,
                WorkOrderPriceTypeEnum.DismantlingCoefficient,
            ].includes(itemType),
    );

    if (isCoefficient) {
        return children;
    }

    return (
        <>
            <div className={Styles.groupContainer}>
                <FieldGroup
                    size="xs"
                    className={cn(Styles.fieldGroup, {
                        [Styles.fieldGroupError]: actFormulaErrors,
                    })}
                >
                    {currentAct?.hasActFormula ? (
                        <div className={Styles.badgeContainer}>
                            <Badge
                                status={actFormulaErrors ? 'error' : 'system'}
                                label={badgeContent}
                                size="xs"
                                className={Styles.badgeContent}
                            />
                        </div>
                    ) : (
                        children
                    )}

                    <Button
                        view="ghost"
                        iconRight={IconEdit}
                        onClick={toggleEditModal}
                        className={cn({
                            [Styles.editFormulaBtn]: !isEditDisabled,
                            [Styles.editFormulaBtnDisabled]: isEditDisabled,
                        })}
                        disabled={isEditDisabled}
                    />
                </FieldGroup>
            </div>

            {isEditOpened && (
                <ActFormulaModal
                    onClose={toggleEditModal}
                    actId={actId}
                    priceId={priceId}
                    refreshDataHandler={refreshDataHandler}
                    itemType={itemType}
                />
            )}
        </>
    );
};

ActFormulaCellRenderer.displayName = 'ActFormulaCellRenderer';
